import { html, nothing } from "lit";
import type { CSSResultGroup } from "lit";

import { customElement, property, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { live } from "lit/directives/live.js";

import "@phosphor-icons/webcomponents/PhEye";
import "@phosphor-icons/webcomponents/PhEyeClosed";

import { inputStyles } from "./input.css.ts";

import "../../button/wow-button.ts";
import "../field-wrapper/field-wrapper.ts";
import { FormElement } from "../form-element.ts";

function getId() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  const length = 6;
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  for (let i = 0; i < length; i++) {
    result += chars[array[i]! % chars.length];
  }
  return result;
}

@customElement("wow-input")
export class WowInput extends FormElement {
  static override styles: CSSResultGroup = [inputStyles];

  @query(".input") private inputElement!: HTMLInputElement;

  @property({ type: String }) label = "";
  @property({ attribute: "help-text" }) helpText = "";
  @property({ attribute: "info-message" }) infoMessage = "";
  @property({ type: String }) placeholder = "";
  @property({ type: String, reflect: true }) override prefix = "";
  @property({ type: String, reflect: true }) suffix = "";

  @property({ reflect: true }) type:
    | "date"
    | "datetime-local"
    | "email"
    | "number"
    | "password"
    | "search"
    | "tel"
    | "text"
    | "time"
    | "url" = "text";

  @property() inputmode: "none" | "text" | "decimal" | "numeric" | "tel" | "search" | "email" | "url" = "text";

  @property() enterkeyhint?: "enter" | "done" | "go" | "next" | "previous" | "search" | "send";

  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, attribute: "show-required-indicator" }) showRequiredIndicator = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: String }) pattern?: string;
  @property({ type: Number }) minlength?: number;
  @property({ type: Number }) maxlength?: number;
  @property({ type: String }) autocomplete: string = "off";

  @property({ attribute: "password-visible", type: Boolean }) passwordVisible = false;
  @property({ attribute: "error-message" }) externalErrorMessage = "";

  @property() min?: number | string;
  @property() max?: number | string;
  @property() step?: number | string;

  detectAutocomplete(event: AnimationEvent) {
    this.focused = event.animationName === "wow-autocomplete-detected";
  }

  override id = getId();

  private handlePasswordToggle = () => {
    this.passwordVisible = !this.passwordVisible;
  };

  public setError(message: string) {
    this._internals.setValidity({ customError: true }, message, this.inputElement);
  }

  public clearError() {
    this._internals.setValidity({});
  }

  public get input(): HTMLInputElement {
    return this.inputElement;
  }

  public showPicker() {
    this.input.showPicker();
  }

  private validateInput(): void {
    if (!this.inputElement) return;

    const validity = this.inputElement.validity;
    const isValid = validity.valid && !this.customError;

    this.hasError = !isValid;

    if (!isValid) {
      this.errorMessage = this.customError || this.inputElement.validationMessage;
      this._internals.setValidity(this.customError ? { customError: true } : validity, this.errorMessage);
    } else {
      this.errorMessage = "";
      this._internals.setValidity({});
    }
  }

  private handleInput(e: Event) {
    this.value = (e.target as HTMLInputElement).value;
  }

  private handleChange(e: Event) {
    this.value = (e.target as HTMLInputElement).value;
    // Native change event has composed: false so it won't cross the shadow boundary.
    // Re-dispatch as a composed input event for iOS Safari where predictive text
    // can insert values without firing the input event.
    this.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
  }

  private handleFocus(e: FocusEvent): void {
    this.focused = true;

    switch (this.type) {
      case "date":
      case "time":
      case "datetime-local": {
        const input = e.target as HTMLInputElement;
        requestAnimationFrame(() => {
          try {
            input.showPicker?.(); // opens native picker if supported
          } catch {
            // silently ignore if unsupported
          }
        });
      }
    }
  }

  private handleBlur(): void {
    this.focused = false;

    if (this.inputmode === "decimal" && typeof this.value === "string") {
      this.value = this.value.replace(/,/g, ".");
      this.inputElement.value = this.value;
    }

    this.validateInput();
  }

  private handleInvalid(event: Event): void {
    event.preventDefault();
    this.validateInput();
  }

  override render() {
    const inputClasses = {
      input: true,
      "with-placeholder": !!this.placeholder,
      "has-value": !!this.value,
      invalid: this.validationMessage,
    };

    const placeholderClasses = {
      "input-placeholder": true,
      active: !!this.value || this.focused,
    };

    let inputType = this.type;

    // iOS-safe decimal handling
    if (this.type === "number" && this.inputmode === "decimal") {
      inputType = "text";
    }

    if (!this.value && !this.focused && inputType !== "text") {
      inputType = "text";
    }

    if (inputType === "password") {
      inputType = this.passwordVisible ? "text" : this.type;
    }

    let inputPattern = this.pattern;
    if (inputType === "url") {
      inputType = "text";
      inputPattern =
        this.pattern ??
        "^(?:[Mm][Aa][Ii][Ll][Tt][Oo]:[^\\s@]+@[^\\s@]+\\.[^\\s@]+|(?:[Hh][Tt][Tt][Pp][Ss]?:\/\/)?(?:(?:[a-zA-Z\u00a1-\uffff0-9]+-?)*[a-zA-Z\u00a1-\uffff0-9]+)(?:\.(?:[a-zA-Z\u00a1-\uffff0-9]+-?)*[a-zA-Z\u00a1-\uffff0-9]+)*(?:\.(?:[a-zA-Z\u00a1-\uffff]{2,}))(?::\\d{2,5})?(?:\/[^\\s]*)?)$";
    }

    const onblur = () => {
      if (this.type !== "url" || this.value === "" || !this.value) return;
      let urlToTest = this.value.trim();

      if (!/^https?:\/\//i.test(urlToTest) && !/^mailto:/i.test(urlToTest)) {
        urlToTest = "https://" + urlToTest;
        this.value = urlToTest;
      }
    };

    return html`
      <wow-field-wrapper>
        ${this.label
          ? html`<label slot="label" class="label" for=${ifDefined(this.id)}
              >${this.label}${this.showRequiredIndicator
                ? html`<span class="required-indicator">*</span>`
                : nothing}</label
            >`
          : nothing}
        ${this.helpText ? html`<span slot="help" class="help-text">${this.helpText}</span>` : nothing}
        ${this.prefix ? html`<span slot="control" class="input-prefix">${this.prefix}</span>` : nothing}

        <input
          .onblur=${onblur}
          id=${ifDefined(this.id)}
          slot="control"
          @animationstart=${this.detectAutocomplete}
          class=${classMap(inputClasses)}
          type=${inputType}
          name=${ifDefined(this.name)}
          .value=${live(this.value)}
          ?required=${this.required}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          pattern=${ifDefined(inputPattern)}
          minlength=${ifDefined(this.minlength)}
          maxlength=${ifDefined(this.maxlength)}
          min=${ifDefined(this.min)}
          max=${ifDefined(this.max)}
          step=${ifDefined(this.step)}
          autocapitalize="off"
          autocomplete=${ifDefined(this.autocomplete as "on")}
          spellcheck="false"
          enterkeyhint=${ifDefined(this.enterkeyhint)}
          inputmode=${ifDefined(this.inputmode)}
          @input=${this.handleInput}
          @change=${this.handleChange}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
          @invalid=${this.handleInvalid}
          aria-label=${ifDefined(this.label || undefined)}
          aria-invalid=${this.hasError ? "true" : "false"}
          aria-describedby=${ifDefined(this.hasError ? "error-message" : undefined)}
        />

        ${this.placeholder
          ? html`<label slot="control" class=${classMap(placeholderClasses)} for=${this.id}>${this.placeholder}</label>`
          : nothing}
        ${this.type === "password" && !this.disabled
          ? html`
              <wow-button
                slot="control"
                class="password-toggle"
                type="button"
                aria-label=${this.passwordVisible ? "hide password" : "show password"}
                @click=${this.handlePasswordToggle}
                tabindex="-1"
                variant="subtle"
              >
                ${this.passwordVisible
                  ? html`<slot name="show-password-icon"><ph-eye-closed></ph-eye-closed></slot>`
                  : html`<slot name="hide-password-icon"><ph-eye></ph-eye></slot>`}
              </wow-button>
            `
          : nothing}
        ${this.suffix ? html`<span slot="control" class="input-suffix">${this.suffix}</span>` : nothing}
        ${this.externalErrorMessage
          ? html`<span class="error-message" slot="error">${this.externalErrorMessage}</span>`
          : this.validationMessage
            ? html`<span class="error-message" slot="error">${this.validationMessage}</span>`
            : this.infoMessage
              ? html`<span class="info-message" slot="error">${this.infoMessage}</span>`
              : nothing}
      </wow-field-wrapper>
    `;
  }
}
